/**
 * ServicingTable — Loan list table for the Interim Servicing dashboard.
 *
 * Displays all servicing loans with key columns, color-coded status badges,
 * and a click handler to open the loan detail panel.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Check, Copy, Loader2, Inbox } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/components/banker-recon/types';
import { toNumber, needsPaymentReview, type ServicingLoan } from '@/types/servicing';
import { formatDateDisplay } from '@/utils/dateHelpers';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ServicingTableProps {
  loans: ServicingLoan[];
  loading: boolean;
  onSelectLoan: (loan: ServicingLoan) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Derive overall loan payment status for badge color. */
function getLoanPaymentStatus(loan: ServicingLoan): 'all_received' | 'overdue' | 'on_track' | 'completed' | 'transferred' {
  if (loan.status === 'transferred') {
    return 'transferred';
  }
  if (loan.status === 'completed') {
    return 'completed';
  }
  if (loan.has_overdue_payments) {
    return 'overdue';
  }

  const received = toNumber(loan.total_received_payments);
  const expected = toNumber(loan.total_expected_payments);
  if (expected > 0 && received >= expected) {
    return 'all_received';
  }

  return 'on_track';
}

/** Get badge variant and label for payment status. */
function getStatusBadge(loan: ServicingLoan): { label: string; className: string } {
  if (needsPaymentReview(loan)) {
    return { label: 'Payment needs review', className: 'bg-warning/10 text-warning-foreground border-warning/30' };
  }
  const status = getLoanPaymentStatus(loan);
  switch (status) {
    case 'all_received':
      return {
        label: 'All Received',
        className: 'bg-emerald-100 text-emerald-800 border-emerald-200'
      };
    case 'overdue':
      return {
        label: 'Overdue',
        className: 'bg-red-100 text-red-800 border-red-200'
      };
    case 'completed':
      return {
        label: 'Completed',
        className: 'bg-slate-100 text-slate-600 border-slate-200'
      };
    case 'transferred':
      return {
        label: 'Transferred',
        className: 'bg-blue-100 text-blue-800 border-blue-200'
      };
    case 'on_track':
    default:
      return {
        label: 'On Track',
        className: 'bg-amber-100 text-amber-800 border-amber-200'
      };
  }
}

/** Derive next due date label from the loan's stored next_payment_due_date. */
function getNextDueLabel(loan: ServicingLoan): string {
  if (loan._source === 'encompass_loans') {
    return '--';
  }
  if (loan.status === 'completed' || loan.status === 'transferred') {
    return '--';
  }
  return loan.next_payment_due_date ? formatDateDisplay(loan.next_payment_due_date) : '--';
}

/** Count received payments from the loan's stored received_payment_count. */
function getReceivedCountLabel(loan: ServicingLoan): string {
  if (loan._source === 'encompass_loans') {
    return '--';
  }
  return `${loan.received_payment_count ?? 0}/${loan.payment_count}`;
}

function LoanNumberCell({ loanNumber }: { loanNumber: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopyLoanNumber = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!loanNumber) {
      return;
    }

    try {
      await navigator.clipboard.writeText(loanNumber);
      setCopied(true);
      toast.success(`Copied ${loanNumber}`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  }, [loanNumber]);

  return (
    <button
      type="button"
      onClick={handleCopyLoanNumber}
      aria-label={`Copy loan number ${loanNumber}`}
      title="Click to copy loan number"
      className="group flex max-w-[120px] cursor-pointer items-center gap-1 truncate hover:text-primary hover:underline"
    >
      <span className="truncate">{loanNumber}</span>
      {copied ? (
        <Check className="h-3 w-3 flex-shrink-0 text-green-500" />
      ) : (
        <Copy className="h-3 w-3 flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-50" />
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ServicingTable({
  loans,
  loading,
  onSelectLoan,
  emptyTitle = 'No servicing loans found',
  emptyDescription = 'Loans will appear here when funded and approaching first payment',
  emptyAction
}: ServicingTableProps) {
  // Sort loans by first_payment_date ascending (the default from the service query)
  const sortedLoans = useMemo(
    () =>
      [...loans].sort((a, b) =>
        (a.first_payment_date || '').localeCompare(b.first_payment_date || '')
      ),
    [loans]
  );

  // -----------------------------------------------------------------------
  // Loading state
  // -----------------------------------------------------------------------

  if (loading && loans.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
        Loading servicing loans...
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Empty state
  // -----------------------------------------------------------------------

  if (loans.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Inbox className="h-10 w-10 mb-3 opacity-40" />
        <p className="text-sm font-medium">{emptyTitle}</p>
        {emptyDescription ? (
          <p className="text-xs mt-1">{emptyDescription}</p>
        ) : null}
        {emptyAction ? (
          <div className="mt-3">{emptyAction}</div>
        ) : null}
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Table
  // -----------------------------------------------------------------------

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[120px]">Loan #</TableHead>
            <TableHead>Borrower</TableHead>
            <TableHead>LO / LO2</TableHead>
            <TableHead>Investor</TableHead>
            <TableHead className="text-center">Purchased</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Rate</TableHead>
            <TableHead>First Payment</TableHead>
            <TableHead>Next Due</TableHead>
            <TableHead className="text-center">Payments</TableHead>
            <TableHead className="text-right">Payment Due</TableHead>
            <TableHead className="text-right">Collected</TableHead>
            <TableHead className="text-center">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedLoans.map((loan) => {
            const badge = getStatusBadge(loan);
            return (
              <TableRow
                key={loan.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onSelectLoan(loan)}
              >
                <TableCell className="font-medium">
                  <LoanNumberCell loanNumber={loan.loan_number} />
                </TableCell>
                <TableCell>{loan.borrower_name || '--'}</TableCell>
                <TableCell>{loan.loan_officer_display || '--'}</TableCell>
                <TableCell>{loan.investor_name || '--'}</TableCell>
                <TableCell className="text-center">
                  <Badge
                    variant="outline"
                    className={loan.investor_purchase_date
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                      : 'bg-amber-100 text-amber-800 border-amber-200'}
                  >
                    {loan.investor_purchase_date ? 'Yes' : 'No'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(toNumber(loan.loan_amount))}
                </TableCell>
                <TableCell className="text-right">
                  {toNumber(loan.interest_rate).toFixed(3)}%
                </TableCell>
                <TableCell>
                  {formatDateDisplay(loan.first_payment_date)}
                </TableCell>
                <TableCell>{getNextDueLabel(loan)}</TableCell>
                <TableCell className="text-center">
                  {getReceivedCountLabel(loan)}
                </TableCell>
                <TableCell className="text-right">
                  {needsPaymentReview(loan) ? 'Needs review' : loan.payment_count > 0
                    ? formatCurrency(toNumber(loan.total_expected_payments) / loan.payment_count)
                    : '--'}
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(toNumber(loan.total_received_payments))}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={badge.className}>
                    {badge.label}
                  </Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
